import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../lib/toast';
import { formatDateTime } from '../lib/formatDate';
import { REQUEST_STATUS_TONE } from '../lib/requestStatus';
import ProposeToolForm from './requests/ProposeToolForm';
import { ToolIcon } from './home/tools/ToolTile';
import CloseIcon from './requests/CloseIcon';
import './requests/requests.css';

const PAGE_SIZE = 10;

// GET /api/tool_requests sends no `scope` field; it flags each row with
// is_requester. Own = the caller submitted it; everything else (team members'
// proposals, ones the caller approves or watches, or every proposal for a
// tool_proposals.publish holder) is "team".
const isOwn = (r, userId) => (typeof r.is_requester === 'boolean' ? r.is_requester : r.user_id === userId);

// Exported for the tabbed shell (RequestsHubPage) and kept as the default
// export for any legacy imports.
export default function RequestsPage() {
  const { user } = useAuth();
  const isDeptManager = (user?.roles || []).includes('dept_manager');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRequest, setEditRequest] = useState(null); // request being edited, or null for create
  const [formBusy, setFormBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [scopeFilter, setScopeFilter] = useState('all'); // all | own | team

  const triggerRef = useRef(null);
  const firstFieldRef = useRef(null);

  const fetchRequests = () => {
    setError('');
    axios.get(`${API_BASE_URL}/api/tool_requests`, { withCredentials: true })
      .then((res) => setRequests(Array.isArray(res.data) ? res.data : []))
      // A failed read is an error, never "you have no tool requests yet".
      .catch((err) => setError(errorMessage(err, "Couldn't load tool proposals. Try again.")))
      .finally(() => setLoading(false));
  };

  const retry = () => { setLoading(true); fetchRequests(); };

  useEffect(() => {
    fetchRequests();
  }, []);

  const openCreateModal = (e) => {
    triggerRef.current = e?.currentTarget || null;
    setEditRequest(null);
    setModalOpen(true);
  };

  const openEditModal = (r, e) => {
    triggerRef.current = e?.currentTarget || null;
    setEditRequest(r);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditRequest(null);
    setFormBusy(false);
  };

  const handleSaved = () => {
    fetchRequests();
    closeModal();
  };

  const visibleRequests = useMemo(() => {
    if (!isDeptManager || scopeFilter === 'all') return requests;
    if (scopeFilter === 'own') return requests.filter((r) => isOwn(r, user?.id));
    if (scopeFilter === 'team') return requests.filter((r) => !isOwn(r, user?.id));
    return requests;
  }, [requests, scopeFilter, isDeptManager, user?.id]);

  const totalPages = Math.max(1, Math.ceil(visibleRequests.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const paginatedRequests = visibleRequests.slice(start, start + PAGE_SIZE);

  const counts = useMemo(() => ({
    all: requests.length,
    own: requests.filter((r) => isOwn(r, user?.id)).length,
    team: requests.filter((r) => !isOwn(r, user?.id)).length,
  }), [requests, user?.id]);

  // Reset to page 1 when data shrinks (e.g. after filter or delete)
  React.useEffect(() => {
    if (visibleRequests.length > 0 && start >= visibleRequests.length) setPage(1);
  }, [visibleRequests.length, start]);

  // Escape-to-close
  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !formBusy) closeModal();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [modalOpen, formBusy]);

  // Focus moves into the drawer on open and back to the button that opened it
  // on close (same pattern as the Build-app "How it works" drawer).
  useEffect(() => {
    if (!modalOpen) return undefined;
    const trigger = triggerRef.current;
    const id = window.setTimeout(() => firstFieldRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(id);
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }, [modalOpen]);

  // Show who submitted each row whenever the list holds anyone else's
  // proposals: dept managers (team) and tool_proposals.publish holders (all).
  const showSubmitter = isDeptManager || requests.some((r) => !isOwn(r, user?.id));

  return (
    <div style={{ width: '100%' }}>
      <div className="rq-section-head">
        <h2 className="rq-section-title">Suggest a tool for the catalog</h2>
        <button type="button" className="btn btn-primary" onClick={openCreateModal}>
          Propose a tool
        </button>
      </div>
      <p className="rq-lead">
        {isDeptManager
          ? "Suggest a tool that isn't in the catalog yet. Your own and your team's proposals are listed below. The manager picked as approver decides first, then an admin adds the tool."
          : "Suggest a tool that isn't in the catalog yet. The manager you pick as approver decides first, then an admin adds the tool."}
      </p>

      {isDeptManager && !error && (
        <div className="rq-chip-group" style={{ marginBottom: 16 }} role="group" aria-label="Request scope">
          {[
            ['all', `All (${counts.all})`],
            ['own', `Mine (${counts.own})`],
            ['team', `Team (${counts.team})`],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={scopeFilter === k}
              className={`rq-chip${scopeFilter === k ? ' is-active' : ''}`}
              onClick={() => { setScopeFilter(k); setPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="rq-loading" role="status" aria-busy="true">Loading proposals…</p>
      ) : error ? (
        <div className="rq-error" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-secondary btn-small" onClick={retry}>Try again</button>
        </div>
      ) : visibleRequests.length === 0 ? (
        <div className="rq-empty">
          <p>
            {isDeptManager && scopeFilter !== 'all'
              ? `No ${scopeFilter === 'own' ? 'proposals of yours' : 'team proposals'} yet.`
              : 'You have no tool requests yet. Select "Propose a tool" to submit one.'}
          </p>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tool name</th>
                {showSubmitter && <th style={{ width: '180px' }}>Submitted by</th>}
                <th>Description / Link</th>
                <th style={{ width: '160px' }}>Status</th>
                <th className="rq-nowrap">Date</th>
                <th style={{ width: '120px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map((r) => {
                const pillClass = (r.status === 'pending' || r.status === 'pending_manager' || r.status === 'pending_admin') ? 'at-pill at-pill--warning' : r.status === 'approved' ? 'at-pill at-pill--success' : 'at-pill at-pill--danger';
                const isMine = isOwn(r, user?.id);
                return (
                  <tr key={r.id}>
                    <td>
                      <div className="rq-tool-cell">
                        <span className="rq-tool-icon"><ToolIcon tool={r} /></span>
                        <span className="at-primary">{r.name}</span>
                      </div>
                    </td>
                    {showSubmitter && (
                      <td>
                        <div className="at-primary">{r.requester_name || '—'}</div>
                        <span className="at-secondary">{r.requester_email}</span>
                        {!isMine && <span className="at-pill at-pill--info" style={{ marginTop: '4px' }}>Team</span>}
                      </td>
                    )}
                    <td className="at-muted">
                      {r.description && <div>{r.description}</div>}
                      {r.link && (() => {
                        const safeLink = /^https?:\/\//i.test(r.link) ? r.link : null;
                        return (
                          <div style={{ marginTop: '4px' }}>
                            {safeLink
                              ? <a className="rq-link" href={safeLink} target="_blank" rel="noopener noreferrer">{r.link}</a>
                              : <span>{r.link}</span>
                            }
                          </div>
                        );
                      })()}
                      {!r.description && !r.link && '—'}
                    </td>
                    <td>
                      <span className={`${pillClass} rq-nowrap`}>{REQUEST_STATUS_TONE[r.status]?.label || r.status}</span>
                      {r.admin_notes && (
                        <div className="at-secondary">Note: {r.admin_notes}</div>
                      )}
                    </td>
                    <td className="at-muted rq-nowrap">
                      {formatDateTime(r.created_at)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.status === 'pending_manager' && isMine && (
                        <button type="button" className="btn btn-small btn-secondary" onClick={(e) => openEditModal(r, e)}>
                          Edit
                        </button>
                      )}
                      {r.status === 'pending_manager' && !isMine && (
                        <span className="at-secondary" title="Only the requester or an admin can edit this.">View only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visibleRequests.length > PAGE_SIZE && (
            <div className="admin-table-footer">
              <span>
                Showing {start + 1}–{Math.min(start + PAGE_SIZE, visibleRequests.length)} of {visibleRequests.length}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Backdrop — always in DOM, toggled via is-open */}
      <div
        className={`pt-drawer-backdrop${modalOpen ? ' is-open' : ''}`}
        onClick={() => { if (!formBusy) closeModal(); }}
        aria-hidden="true"
      />

      {/* Drawer — always in DOM so the slide-in transition plays */}
      <aside
        className={`pt-drawer${modalOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pt-drawer-title"
        aria-hidden={!modalOpen}
        // Closed, the drawer stays mounted (so it can slide) but is only moved
        // off-screen; inert takes it out of the tab order and the a11y tree.
        // React 18 does not know `inert`, so it is passed as the string ''
        // (`inert={true}` warns). React 19 makes it a boolean prop and treats
        // '' as FALSE: on that upgrade this must become `inert={!modalOpen}`,
        // or the closed drawer silently becomes focusable again.
        {...(modalOpen ? {} : { inert: '' })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-drawer-header">
          <h2 id="pt-drawer-title">{editRequest ? 'Edit proposal' : 'Propose a tool'}</h2>
          <button
            type="button"
            className="pt-drawer-close"
            onClick={closeModal}
            disabled={formBusy}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="pt-drawer-body">
          {modalOpen && (
            <ProposeToolForm
              initial={editRequest ? {
                name: editRequest.name,
                description: editRequest.description || '',
                link: editRequest.link || '',
                icon_url: editRequest.icon_url || '',
              } : null}
              editId={editRequest ? editRequest.id : null}
              onSaved={handleSaved}
              onCancel={closeModal}
              onBusyChange={setFormBusy}
              firstFieldRef={firstFieldRef}
            />
          )}
        </div>
      </aside>
    </div>
  );
}
